import { times as _times } from 'lodash-es'
import { Model, Factory } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'

import type { SchemaAttributes } from '../types.js'
import type { MockServer, MirageServer } from './server.js'
import { pageable } from './utils.js'

export type ActivePermissionsListData =
  SchemaAttributes<'ActivePermissionsList'>

export const model = Model as ModelDefinition<ActivePermissionsListData>

export const factory = Factory.extend<ActivePermissionsListData>({
  permissions() {
    return _times(10, (i) => ({
      key: `permission-${i}`,
      read: true,
      write: true,
    }))
  },
})

export function routes(server: MockServer) {
  server.get(
    '/active_permissions/current',
    function (this: MirageServer, schema, request) {
      const { data, meta } = pageable(
        schema.all('activePermissionsList'),
        request,
      )

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  const permissionOptions = [
    'add-health-plan-subscription',
    'add-refill-outside-consultation',
    'add-unapproved-feedback',
    'add-unapproved-referral-report',
    'always-edit-consultation-notes',
    'appointments-access',
    'appointments-dayplan',
    'appointments-page',
    'approve-discharge-instructions',
    'approve-feedback',
    'approve-referral-report',
    'approve-student-notes',
    'bookingcapacity-page',
    'can-edit-other-users-availability-status',
    'cancel-health-plan-subscription',
    'cancel-payment',
    'cancel-refills',
    'catalog-management',
    'changelog',
    'change-locum-permission-group',
    'change-veterinarian-on-finalized-invoice',
    'client-prepayment-date',
    'clinical-notes',
    'complication-report',
    'consultation-delete',
    'consultations',
    'creat-credit-note-from-credit-note',
    'credit-note',
    'credit-note-and-invoice-again',
    'credit-note-to-prepayment-transfer',
    'credit-note-with-insurance',
    'dashboard-expiring-reminders',
    'dashboard-laboratory-anayses',
    'dashboard-laboratory-referrals',
    'dashboard-pharmacy',
    'dashboard-recent-consultations',
    'dashboard-scheduled-appointments',
    'dashboard-shared-inbox',
    'delete-clients',
    'delete-insurance-claim',
    'delete-insurance-claim-on-finalized-invoices',
    'delete-laboratory-analysis',
    'delete-purchase-orders',
    'diagnostic_imaging-dashboard',
    'diagnostic_imaging-referrals',
    'discharge-instructions',
    'discharge-instructions-ai-summary',
    'edit-handling-and-injection-fee-settings',
    'edit-locked-notes',
    'edit-others-notes',
    'edit-purchase-orders',
    'edit-stock-locations',
    'edit-stock-sharing-settings',
    'edit-wholesalers',
    'email-feedback',
    'email-unapproved-discharge-instructions',
    'estimate',
    'estimate-bundles',
    'finder-page',
    'insurance',
    'invoce-price-edit',
    'invoice',
    'invoice-date',
    'invoice-end-of-day',
    'invoice-negative-amount',
    'invoice-payment-date',
    'invoice-price-edit-on-procedures',
    'laboratory-access-request-detail',
    'laboratory-approve_reports',
    'laboratory-approve_results',
    'laboratory-dashboard',
    'laboratory-lock-referrals',
    'laboratory-microbiology',
    'laboratory-referrals',
    'manage-locum',
    'mark-as-private',
    'medvind',
    'move-stock-between-departments',
    'notes-and-communication',
    'notes-and-communication-can-delete',
    'notes-and-communication-see-deleted',
    'notification',
    'patient-referrals',
    'patient-referrals-dashboard',
    'patients-clients',
    'payment-gateway-pay-manage',
    'payment-gateway-pay-payments',
    'payment-gateway-pay-reports',
    'payment-gateway-pay-tokenized',
    'prepayment-transfer',
    'prescription-edit',
    'prescription-see-fees-from-all-users',
    'reminder',
    'reports-business-intelligence',
    'reports-communication',
    'reports-financial',
    'reports-financial-bank-payment-processing',
    'reports-financial-cl-month-end',
    'reports-financial-cl-referral-rows',
    'reports-financial-commissions',
    'reports-financial-debtors_and_creditors',
    'reports-financial-discounts',
    'reports-financial-electronic-journal',
    'reports-financial-epresc-fees',
    'reports-financial-gross-profit',
    'reports-financial-invoices',
    'reports-financial-item-purchases',
    'reports-financial-item-sales',
    'reports-financial-month-end',
    'reports-financial-payments',
    'reports-financial-prepayments',
    'reports-financial-sales-by-payment',
    'reports-financial-unpaid-invoices',
    'reports-lists',
    'reports-my-statistics',
    'reports-page',
    'reports-practice-performance',
    'reports-stock-management',
    'require-two-factor-authentication',
    'settings-bundles',
    'settings-central-items',
    'settings-client-communication',
    'settings-commissions',
    'settings-custom-fields',
    'settings-custom-status-fields',
    'settings-department',
    'settings-department-management',
    'settings-discounts',
    'settings-export-functions',
    'settings-group-administration',
    'settings-health-plan',
    'settings-import-functions',
    'settings-integration',
    'settings-integration-log',
    'settings-item-alternative-pricing',
    'settings-item-attachments',
    'settings-item-cost-centers',
    'settings-item-creation-rules',
    'settings-item-description',
    'settings-item-linked-items',
    'settings-item-linked-laboratory-analyses',
    'settings-item-medicine-instruction-templates',
    'settings-item-options',
    'settings-item-pricing',
    'settings-item-pricing-options',
    'settings-item-reference-values',
    'settings-item-stock-alert-level',
    'settings-item-stock-options',
    'settings-item-vaccine-options',
    'settings-item-waste-management',
    'settings-item-wholesalers',
    'settings-item-withdrawal-periods',
    'settings-items',
    'settings-items-create',
    'settings-items-delete',
    'settings-lists',
    'settings-markup',
    'settings-organization',
    'settings-page',
    'settings-print',
    'settings-proflow',
    'settings-reasons',
    'settings-reminders',
    'settings-revision-log',
    'settings-setting-log',
    'settings-shift-templates',
    'settings-shift-types',
    'settings-stock',
    'settings-subscription',
    'settings-tags',
    'settings-templates',
    'settings-user-administration',
    'settings-user-log',
    'settings-vat-management',
    'settings-webhook-via-api',
    'shifts-access',
    'shifts-page',
    'statement',
    'task',
    'tasks-of-all-departments',
    'treatment_plan-dashboard',
    'treatment_plan-item',
    'unapproved-clinical-notes',
    'unapproved-discharge-instructions',
    'update-health-plan-status',
    'upload-read-all',
    'use-client-on-stop',
    'use-dispense-fee',
    'user-api-management',
    'user-calendar-feeds',
    'user-change-password',
    'user-edit-email',
    'user-edit-initials',
    'user-mobile-app-settings',
    'user-multifactor-authentication',
    'user-personal-details',
    'user-preferences',
    'user-print-settings',
    'user-profile-image',
    'user-profile-page',
    'user-task-settings',
    'user-theme',
    'user-widget-settings',
    'workspace',
  ]
  const permissions = permissionOptions.map((permission) => ({
    key: permission,
    read: true,
    write: true,
  }))

  server.create('activePermissionsList', {
    permissions,
  })
}
